import { PAGES_WITH_OLD_URLS_QUERY } from "apollo/queries/strings/pagesWithOldUrls"
import { graphqlFetch } from "./graphqlFetch"
import { getNonUmbracoUrl } from "./getNonUmbracoUrl"
import {
  compileRedirects,
  emptyCompiledRedirects,
  normalizePath,
  type CompiledRedirects,
  type Redirect,
} from "./redirectTypes"

const GRAPHQL_URL = process.env.GRAPHQL_URL
if (!GRAPHQL_URL) {
  throw new Error("Please provide the GRAPHQL_URL environment variable.")
}

/** Matches the previous cache window. The constant used to be misnamed `oneDayInMillis`. */
const TTL_MS = 60 * 60 * 1000

/**
 * Hard ceiling on the refresh. This runs on the first request after process boot,
 * so it is the only thing a user can ever wait on, and it must stay far below the
 * F5 504 threshold.
 */
const FETCH_TIMEOUT_MS = 5000

/**
 * Retry window after a failed refresh. Without it, a failed first refresh leaves
 * the map null and every subsequent request awaits its own pair of queries.
 */
const FAILURE_BACKOFF_MS = 60 * 1000

const CULTURES = ["he-il", "en-us"] as const

type CacheState = {
  lastFetch: number
  compiled: CompiledRedirects | null
  /** Flat list kept for the legacy `getCachedRedirects` signature. */
  raw: Redirect[] | null
  inFlight: Promise<void> | null
}

type GlobalWithCache = typeof globalThis & {
  __oldUrlRedirectsCache?: CacheState
}

const globalWithCache = global as GlobalWithCache

const state: CacheState = (globalWithCache.__oldUrlRedirectsCache ??= {
  lastFetch: 0,
  compiled: null,
  raw: null,
  inFlight: null,
})

type OldUrlsResponse = {
  components?: {
    edges?: Array<{ node?: OldUrlsNode }>
  }
}

type OldUrlsNode = {
  url?: string
  properties?: Array<{
    propertyName?: string
    propertyValue?: { links?: Array<{ url?: string }> }
  }>
  children?: OldUrlsNode[]
}

const stripCulture = (value: string): string =>
  value
    .replace(/\/$/, "")
    .replace(/\/he-IL$/, "")
    .replace(/\/en-US$/, "")

/** Absolute `oldUrls` values are stored with the domain. Only the path is usable. */
const toPath = (value: string): string => {
  if (!value.startsWith("http://") && !value.startsWith("https://")) return value
  try {
    return new URL(value).pathname
  } catch {
    return value
  }
}

function extractOldUrls(nodes: OldUrlsNode[]): Redirect[] {
  const redirects: Redirect[] = []

  nodes.forEach((node) => {
    if (Array.isArray(node.properties)) {
      node.properties.forEach((property) => {
        if (property.propertyName !== "oldUrls") return

        property.propertyValue?.links?.forEach((link) => {
          if (!link.url || !node.url) return

          const source = stripCulture(toPath(link.url))
          const destination = getNonUmbracoUrl(stripCulture(node.url))

          if (source && destination) redirects.push({ source, destination })
        })
      })
    }

    if (node.children?.length) {
      redirects.push(...extractOldUrls(node.children))
    }
  })

  return redirects
}

type CultureResult = {
  redirects: Redirect[] | null
  /** A 4xx means the query is wrong for this schema, so a quick retry cannot help. */
  permanentFailure: boolean
}

async function fetchCulture(pathBase: string, culture: string): Promise<CultureResult> {
  const { data, error, permanentFailure } = await graphqlFetch<OldUrlsResponse>({
    url: GRAPHQL_URL as string,
    query: PAGES_WITH_OLD_URLS_QUERY,
    operationName: "GetPagesWithOldURLs",
    variables: { route: pathBase, culture },
    timeoutMs: FETCH_TIMEOUT_MS,
  })

  if (error || !data) {
    console.error(`[redirects] ${culture} refresh failed for ${pathBase}:`, error?.message)
    return { redirects: null, permanentFailure: Boolean(permanentFailure) }
  }

  const nodes = (data.components?.edges ?? [])
    .map((edge) => edge?.node)
    .filter((node): node is OldUrlsNode => Boolean(node))

  return { redirects: extractOldUrls(nodes), permanentFailure: false }
}

/**
 * A transient failure retries after a short window. A permanent one waits the full
 * TTL, otherwise an environment whose schema rejects the query logs and queries
 * forever at many times the success-path rate.
 */
function applyFailureBackoff(permanent: boolean): void {
  state.lastFetch = permanent ? Date.now() : Date.now() - TTL_MS + FAILURE_BACKOFF_MS
}

/**
 * Cache an empty map rather than leaving it null. A null map makes every request
 * block on its own refresh; an empty map means no redirects, which is the correct
 * degraded behaviour and costs nothing.
 */
function degradeToEmptyMap(): void {
  state.raw = []
  state.compiled = emptyCompiledRedirects()
}

async function refresh(pathBase: string): Promise<void> {
  // Both cultures in parallel. They used to be awaited one after the other, which
  // doubled the window in which every concurrent request started its own pair.
  const results = await Promise.all(CULTURES.map((culture) => fetchCulture(pathBase, culture)))

  // A failed culture must not silently halve the redirect table.
  if (results.some((result) => result.redirects === null)) {
    applyFailureBackoff(results.some((result) => result.permanentFailure))

    if (state.compiled) {
      console.error("[redirects] refresh incomplete, keeping the previous map")
      return
    }

    console.error("[redirects] first refresh incomplete, serving an empty map")
    degradeToEmptyMap()
    return
  }

  // Precedence is deliberate. The previous implementation concatenated as
  // [...enUrls, ...heUrls], so an en-us entry won a duplicate source. Compilation
  // is first-writer-wins, so the same order has to be preserved here.
  const [heResult, enResult] = results
  const raw = [...(enResult.redirects ?? []), ...(heResult.redirects ?? [])]

  state.raw = raw
  state.compiled = compileRedirects(raw, { permanent: true })
  state.lastFetch = Date.now()
}

/**
 * Single flight. Without this, every concurrent request arriving after the TTL
 * expires starts its own pair of CMS queries.
 */
function startRefresh(pathBase: string): Promise<void> {
  state.inFlight ??= refresh(pathBase)
    .catch((error) => {
      // Without this the map stays null after a throw, so every subsequent request
      // runs its own pair of queries with no backoff at all.
      console.error("[redirects] refresh threw:", error)
      applyFailureBackoff(false)
      if (!state.compiled) degradeToEmptyMap()
    })
    .finally(() => {
      state.inFlight = null
    })

  return state.inFlight
}

/**
 * Compiled `oldUrls` redirects for the app rooted at `pathBase`.
 *
 * Stale while revalidate: after the first successful load no request ever waits
 * on the CMS. Only the first request after process boot can block, capped at
 * `FETCH_TIMEOUT_MS`, falling back to an empty map.
 */
export async function getCompiledRedirects(pathBase: string): Promise<CompiledRedirects> {
  const isStale = Date.now() - state.lastFetch > TTL_MS

  if (!state.compiled) {
    await startRefresh(pathBase)
    return state.compiled ?? emptyCompiledRedirects()
  }

  if (isStale) {
    // Fire and forget. The caller gets the previous map immediately.
    void startRefresh(pathBase)
  }

  return state.compiled
}

/**
 * Currently cached compiled map, without triggering a fetch. Used by
 * `landingRedirectsCache` to report source collisions between the two systems.
 */
export function peekCompiledRedirects(): CompiledRedirects | null {
  return state.compiled
}

/**
 * Flat redirect list. Kept for backwards compatibility with the previous export;
 * `getCompiledRedirects` is what the middleware should use.
 */
export async function getCachedRedirects(pathBase: string): Promise<Redirect[] | null> {
  await getCompiledRedirects(pathBase)
  return state.raw
}

// Function to replace dynamic path placeholders, or return direct destination for plain URLs
export const replacePath = (destination: string, params?: Record<string, string>): string => {
  if (!params) {
    return destination
  } // Return the direct destination if there are no parameters to replace
  return destination.replace(/:\w+\*/g, (match) => {
    const key = match.slice(1, -1) // Remove the colon and asterisk
    return params[key] || "" // Replace with the parameter value or empty string
  })
}

// Extract dynamic segments from the path or return {} for a direct match
export const extractParams = (pattern: string, path: string): Record<string, string> | null => {
  const source = normalizePath(pattern)
  const current = normalizePath(path)

  if (!source.includes(":")) {
    return source === current ? {} : null // Direct match for plain URLs
  }

  const regex = new RegExp(`^${source.replace(/:\w+\*/g, "(.*)")}$`, "i")
  const match = current.match(regex)

  return match ? { path: match[1] } : null // Assuming only one dynamic segment for simplicity
}
